import axios from 'axios'
import moment from 'moment'

const RESET = 'RESET'
const SET_USER_EMAILS = 'SET_USER_EMAILS'

const ADD_QUERY = 'ADD_QUERY'
const UPDATE_QUERY = 'UPDATE_QUERY'
const REMOVE_QUERY = 'REMOVE_QUERY'
const SET_QUERIES = 'SET_QUERIES'
const SET_TOTAL_QUERIES = 'SET_TOTAL_QUERIES'
const SET_REPORT_GENERATOR = 'SET_REPORT_GENERATOR'
const UPDATE_MISSING_PARAMS = 'UPDATE_MISSING_PARAMS'
const SET_SERVICE_ACTIVE = 'SET_SERVICE_ACTIVE'

const fetchStationAnalytesPath = '/api/MultiStationQuery/Details/'
const fetchStationsGuidelinesPath = '/api/MultiStationQuery/StationsGuidelines'
const fetchContinuousStationsTimeSeriesPath = '/api/MultiStationQuery/ContinuousStationsTimeSeries'
const getAnalyteMapPath = '/api/MultiStationQuery/AnalyteMapping/'
const generateReportPath = '/api/MultiStationQuery/GenerateReport/'
const generateSilvertipBCMOEReportPath = '/api/customReports/SilvertipBCMOE'
const generateContinuousDataReportPath = '/api/MultiStationQuery/GenerateContinuousDataReport/'

import { SERVICE_LINKS } from '@/store/ServiceRegistry/linkedservices'
import { StationMediaTypeGrouping } from '@/helpers/multiStationQuery'

let initialState = () => ({
  reportGenerator: null,
  isServiceActive: false,
  usersDetail: {},
  totalQueries: 0,
  queries: [],

  missingParams: {
    stations: [],
    analytes: [],
    guidelines: [],
  },
})

const makeGuidelines = (data) => {
  let result = {}
  result.Guidelines = data.Guidelines.map(guideline => ({
    ...guideline,
    MediaTypes: new Set(guideline.MediaTypes),
  }))

  result.StationMediaTypes = new StationMediaTypeGrouping(data.StationMediaTypeGrouping)
  return result
}

const makeAnalytes = (data) => {
  let reasonableDate = moment().add(-1000, 'years')
  Object.keys(data).forEach(key => {
    let analyte = data[key]
    analyte.FirstSampleDate = moment(analyte.FirstSampleDate)
    analyte.LastSampleDate = moment(analyte.LastSampleDate)

    if (analyte.FirstSampleDate.isBefore(reasonableDate)) {
      analyte.FirstSampleDate = null
    }
    if (analyte.LastSampleDate.isBefore(reasonableDate)) {
      analyte.LastSampleDate = null
    }

    analyte.StationIds = new Set(analyte.StationIds)
  })

  return data
}

export default {
  namespaced: true,
  state: initialState,
  actions: {
    fetchStationsAnalytes(_, selection) {
      return axios.post(fetchStationAnalytesPath, selection)
        .then(response => {
          return makeAnalytes(response.data)
        })
    },
    fetchStationsGuidelines() {
      return axios.get(fetchStationsGuidelinesPath)
        .then(response => {
          return makeGuidelines(response.data)
        })
    },
    fetchContinuousStationsTimeSeries() {
      return axios.get(fetchContinuousStationsTimeSeriesPath)
        .then(response => {
          return makeAnalytes(response.data)
        })
    },
    async fetchQueries({
      commit,
      dispatch,
    }, {
      service,
      tenantId,
      params,
    }) {
      try {
        const response = await dispatch('serviceconfigurations/fetchTenantConfigurations', {
          service,
          tenantId,
          params,
        }, {
          root: true,
        })

        const {
          configurations,
          total,
        } = response.data

        commit(SET_QUERIES, configurations)
        commit(SET_TOTAL_QUERIES, total)
      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    async fetchQuery({
      commit,
      dispatch,
    }, {
      service,
      query,
      queryId,
    }) {
      try {
        const response = await dispatch('serviceconfigurations/fetchConfiguration', {
          service,
          configurationId: queryId || query.id,
          params: {
            fields: 'queryParams',
          },
        }, {
          root: true,
        })

        const data = response.data
        commit(UPDATE_QUERY, data)
      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    async fetchReportGenerator({
      commit,
      dispatch,
    }) {
      try {
        let reportGenerator = await dispatch('linkedservices/getServiceByLink', {
          serviceLink: SERVICE_LINKS.DISCRETE_DATA_QUERY_REPORT,
        }, {
          root: true,
        })

        commit(SET_REPORT_GENERATOR, reportGenerator)

      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    async pingService({
      commit,
      getters,
      dispatch,
    }) {
      if (!getters.reportGenerator) {
        commit(SET_SERVICE_ACTIVE, false)
        return
      }

      let serviceId = getters.reportGenerator.Id
      let pingResponse = await dispatch('services/pingServices', {
        serviceId,
      }, {
        root: true,
      })

      commit(SET_SERVICE_ACTIVE, pingResponse.Active)
    },
    getAnalyteMapping(_, analyteIds) {
      return axios.post(getAnalyteMapPath, {
        AnalyteIds: analyteIds,
      })
        .then(response => {
          return response.data
        })
    },
    generateReport(_, requestData) {
      const cancelToken = axios.CancelToken
      const source = cancelToken.source()

      let responsePromise = axios({
        method: 'post',
        url: generateReportPath,
        data: requestData,
        cancelToken: source.token,
      })
        .then(response => {
          return response.data
        })

      return {
        responsePromise,
        source,
      }
    },
    generateContinuousDataReport(_, requestData) {
      const cancelToken = axios.CancelToken
      const source = cancelToken.source()

      let responsePromise = axios({
        method: 'post',
        url: generateContinuousDataReportPath,
        data: requestData,
        cancelToken: source.token,
      })
        .then(response => {
          return response.data
        })

      return {
        responsePromise,
        source,
      }
    },
    async generateSilvertipBCMOEReport({ dispatch }, requestData) {
      try {
        let response = await dispatch('services/executeServiceProxy', {
          payload: requestData,
          subpath: generateSilvertipBCMOEReportPath,
          serviceLink: SERVICE_LINKS.DISCRETE_DATA_QUERY_REPORT,
        }, {
          root: true,
        })

        return response.data
      } catch (error) {
        console.error(error)
        throw error.response.data
      }
    },
    async fetchUsersDetail({
      commit,
      dispatch,
    }, userIds) {
      let result = await dispatch('admin/getUsersDetail', userIds, {
        root: true,
      })

      commit(SET_USER_EMAILS, result)
    },
    async addQuery({
      commit,
      dispatch,
    }, {
      service,
      query,
    }) {
      try {
        let response = await dispatch('serviceconfigurations/addConfiguration', {
          service,
          configuration: query,
        }, {
          root: true,
        })

        let data = response.data
        commit(ADD_QUERY, data)
        return data
      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    async updateQuery({
      commit,
      dispatch,
    }, {
      service,
      query,
    }) {
      try {
        let response = await dispatch('serviceconfigurations/updateConfiguration', {
          service,
          configuration: query,
        }, {
          root: true,
        })

        let data = response.data
        commit(UPDATE_QUERY, data)
        return data
      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    async removeQuery({
      commit,
      dispatch,
    }, {
      service,
      query,
    }) {
      try {
        await dispatch('serviceconfigurations/deleteConfiguration', {
          service,
          configuration: query,
        }, {
          root: true,
        })
        commit(REMOVE_QUERY, query)
      } catch (e) {
        console.error(e)
        throw e.response ? e.response.data : e
      }
    },
    async checkExisting({
      dispatch,
    }, {
      service,
      payload,
    }) {
      try {
        let response = await dispatch('serviceconfigurations/validateConfiguration', {
          service,
          payload,
        }, {
          root: true,
        })

        const {
          exists,
        } = response.data
        return exists
      } catch (e) {
        console.error(e)
        throw e.response.data
      }
    },
    updateMissingParams({
      commit,
    }, properties) {
      commit(UPDATE_MISSING_PARAMS, properties)
    },
    clearMissingParams({
      commit,
    }) {
      commit(UPDATE_MISSING_PARAMS, initialState().missingParams)
    },
    generateMoeReportAsync(_, data) {
      return axios.post('api/AsyncReport/MOEReport', data)
        .then(response => {
          return response.data
        })
    },
    generateDDQReportAsync(_, data) {
      return axios.post('api/AsyncReport/DDQReport', data)
        .then(response => {
          return response.data
        })
    },
    generateCDQReportAsync(_, data) {
      return axios.post('api/AsyncReport/CDQReport', data)
        .then(response => {
          return response.data
        })
    },
  },
  mutations: {
    [SET_SERVICE_ACTIVE](state, active) {
      state.isServiceActive = active
    },
    [UPDATE_MISSING_PARAMS](state, properties) {
      state.missingParams = {
        ...state.missingParams,
        ...properties,
      }
    },
    [SET_USER_EMAILS](state, usersDetail) {
      state.usersDetail = usersDetail
    },
    [RESET](state) {
      const s = initialState()
      Object.keys(s).forEach(key => {
        state[key] = s[key]
      })
    },
    [ADD_QUERY](state, query) {
      state.queries.push(query)
    },
    [UPDATE_QUERY](state, query) {
      let index = state.queries.findIndex(q => q.id == query.id)
      state.queries.splice(index, 1, query)
    },
    [REMOVE_QUERY](state, query) {
      let index = state.queries.findIndex(q => q.id == query.id)
      state.queries.splice(index, 1)
    },
    [SET_QUERIES](state, queries) {
      state.queries = queries
    },
    [SET_TOTAL_QUERIES](state, totalQueries) {
      state.totalQueries = totalQueries
    },
    [SET_REPORT_GENERATOR](state, reportGenerator) {
      state.reportGenerator = reportGenerator
    },
  },
  getters: {
    usersDetail: (state) => state.usersDetail,
    queries: (state) => state.queries,
    totalQueries: (state) => state.totalQueries,
    reportGenerator: (state) => state.reportGenerator,
    isServiceActive: (state) => state.isServiceActive,
    missingParams: (state) => state.missingParams,
  },
}